import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import partnerService from 'services/partnerService';
import contactNoteService from 'services/contactNoteService';
import { DropdownOption } from 'components/Common/ValidatedDropdown';
import contactNoteDialogService from 'services/contactNoteDialogService';

export interface UserDropdownOption extends DropdownOption {
  firstName: string;
  lastName: string;
  userId: string;
}

export interface ResourceValues {
  countries: { value: string; label: string }[];
  employeeOptions: { label: string; value: string }[];
  initiatedByOptions: { label: string; value: string }[];
  topicsOptions: { label: string; value: string }[];
  contactTypeOptions: { label: string; value: string }[];
}

export const fetchInitEnumValues = createAsyncThunk<ResourceValues, void, { rejectValue: any }>(
  'RESOURCES/fetchInitEnumValues',
  async (_, thunkAPI) => {
    try {
      const countries = await partnerService.getCountries();
      const employeeOptions = await contactNoteService.getEmployeeOptions();
      const mappedEmployeeOptions = employeeOptions.map((user: UserDropdownOption) => ({
        label: user.firstName + ' ' + user.lastName + ' (' + user.userId + ')',
        value: user.userId,
        name: user.firstName + ' ' + user.lastName,
      }));
      const [initiatedByOptions, contactTypeOptions, topicsOptions] =
        await contactNoteDialogService.getDropdownOptions();
      return {
        countries,
        employeeOptions: mappedEmployeeOptions,
        initiatedByOptions,
        contactTypeOptions,
        topicsOptions,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue((error as any).response);
    }
  },
);

const initialResourceState: ResourceValues = {
  countries: [],
  employeeOptions: [],
  initiatedByOptions: [],
  topicsOptions: [],
  contactTypeOptions: [],
};

const resourcesSlice = createSlice({
  name: 'RESOURCES',
  initialState: initialResourceState,
  reducers: {
    initEnumValues: (state, action: PayloadAction<ResourceValues>) => {
      return {
        ...state,
        countries: action.payload.countries,
        employeeOptions: action.payload.employeeOptions,
        initiatedByOptions: action.payload.initiatedByOptions,
        contactTypeOptions: action.payload.contactTypeOptions,
        topicsOptions: action.payload.topicsOptions,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInitEnumValues.fulfilled, (state, action: PayloadAction<ResourceValues>) => {
      state.countries = action.payload.countries;
      state.employeeOptions = action.payload.employeeOptions;
      state.initiatedByOptions = action.payload.initiatedByOptions;
      state.contactTypeOptions = action.payload.contactTypeOptions;
      state.topicsOptions = action.payload.topicsOptions;
    });
  },
});

export const { initEnumValues } = resourcesSlice.actions;

export default resourcesSlice.reducer;
